import type { WidgetId } from '@wix/members-area-app-definitions';
import type { PageRef } from '@wix/platform-editor-sdk';
import type { FlowEditorSDK } from '@wix/yoshi-flow-editor';

import {
  PROFILE_WIDGET_OPTIONS,
  SETTINGS_WIDGET_OPTIONS,
} from '../../../constants';
import { EDITOR_X_WIDGET_LAYOUT } from '../../../constants/editor-x';
import {
  E3_PROFILE_WIDGET_OPTIONS,
  E3_SETTINGS_WIDGET_OPTIONS,
} from '../../../constants/widget';
import { addWidget } from '../../../editor/editor-sdk-wrappers';
import {
  toMonitored,
  transactionWithConflictMonitor,
} from '../../../editor/services/monitor';
import {
  createProfilePage,
  createSettingsPage,
  findSectionInPage,
} from '../../../editor/services/page';
import {
  getProfilePageBobWidgetRef,
  getSettingsPageWidgetRef,
} from '../../../editor/services/page-ref';
import type { PlatformContext, RouteConfiguration } from '../../../types';
import { getIsEditor3 } from '../utils';
import { removeWidgetsMenusItems } from './menu-items';
import { connectMemberProfileSubMenuToInnerElements } from './profile-page-sub-menu';
import {
  getProfilePageRoutes,
  getSettingsPageRoutes,
  removeRoutesFromProfilePage,
  removeRoutesFromSettingsPage,
} from './routes';
import { connectMemberSettingsSubMenuToInnerElements } from './settings-page-sub-menu';
import { removeWidgetPluginsFromWidget } from './slots';

const getSectionRef = (
  context: Pick<PlatformContext, 'editorSDK' | 'flowAPI'>,
  pageRef: PageRef,
) => {
  const { environment } = context.flowAPI;

  if (environment.isEditorX) {
    return;
  }

  return findSectionInPage(context, pageRef);
};

export const addMemberSettingsPage = async (
  context: Pick<PlatformContext, 'flowAPI' | 'editorSDK'>,
) => {
  const { editorSDK } = context;
  const isEditor3 = getIsEditor3();

  const action = async () => {
    const pageRef = await toMonitored(
      'ma-split.install.create-profile-page',
      () => createSettingsPage(context),
    );
    const sectionRef = await toMonitored(
      'ma-split.install.get-section-ref',
      () => getSectionRef(context, pageRef),
    );
    const containerRef = sectionRef || pageRef;

    const widgetOptions = isEditor3
      ? E3_SETTINGS_WIDGET_OPTIONS
      : SETTINGS_WIDGET_OPTIONS;

    const widgetRef = await addWidget(editorSDK, {
      ...widgetOptions,
      layouts: EDITOR_X_WIDGET_LAYOUT,
      containerRef,
    });

    if (isEditor3) {
      await connectMemberSettingsSubMenuToInnerElements(editorSDK, widgetRef);
    }

    return widgetRef;
  };

  return transactionWithConflictMonitor(
    context.editorSDK,
    'ma-split.add-settings-bob-widget',
    action,
  );
};

export const addProfilePageWidget = async (
  context: Pick<PlatformContext, 'flowAPI' | 'editorSDK'>,
) => {
  const { flowAPI, editorSDK } = context;
  const { t } = flowAPI.translations;
  const isEditor3 = getIsEditor3();

  const transaction = async () => {
    const profilePageRef = await toMonitored(
      'ma-split.install.create-profile-page',
      () => {
        return createProfilePage({
          flowAPI,
          editorSDK,
          title: t('app.page.profile.title'),
          pageUriSEO: 'profile',
        });
      },
    );
    const sectionRef = await toMonitored(
      'ma-split.install.get-section-ref',
      () => getSectionRef(context, profilePageRef),
    );
    const containerRef = sectionRef || profilePageRef;

    const widgetOptions = isEditor3
      ? E3_PROFILE_WIDGET_OPTIONS
      : PROFILE_WIDGET_OPTIONS;

    const widgetRef = await addWidget(editorSDK, {
      ...widgetOptions,
      layouts: EDITOR_X_WIDGET_LAYOUT,
      containerRef,
    });

    if (isEditor3) {
      await connectMemberProfileSubMenuToInnerElements(editorSDK, widgetRef);
    }

    return widgetRef;
  };

  return transactionWithConflictMonitor(
    editorSDK,
    'ma-split.add-profile-page-bob-widget',
    transaction,
  );
};

const groupWidgetIdsByLocation = (
  widgetIds: WidgetId[],
  settingsRoutes: RouteConfiguration[],
  profileRoutes: RouteConfiguration[],
) => {
  const settingsWidgets: WidgetId[] = [];
  const profileWidgets: WidgetId[] = [];

  widgetIds.forEach((widgetId) => {
    if (settingsRoutes.some((route) => route.widgetId === widgetId)) {
      settingsWidgets.push(widgetId);
    } else if (profileRoutes.some((route) => route.widgetId === widgetId)) {
      profileWidgets.push(widgetId);
    }
  });

  return { settingsWidgets, profileWidgets };
};

export const removeWidgetPlugins = async (
  editorSDK: FlowEditorSDK,
  widgetsIds: WidgetId[],
) => {
  const [settingsRoutes, profileRoutes] = await Promise.all([
    getSettingsPageRoutes(editorSDK),
    getProfilePageRoutes(editorSDK),
  ]);
  const { profileWidgets, settingsWidgets } = groupWidgetIdsByLocation(
    widgetsIds,
    settingsRoutes,
    profileRoutes,
  );
  const [profilePageWidgetRef, settingsPageWidgetRef] = await Promise.all([
    profileRoutes.length > 0 ? getProfilePageBobWidgetRef(editorSDK) : null,
    settingsRoutes.length > 0 ? getSettingsPageWidgetRef(editorSDK) : null,
  ]);

  if (settingsPageWidgetRef && settingsWidgets.length > 0) {
    await removeWidgetPluginsFromWidget(
      editorSDK,
      settingsPageWidgetRef,
      widgetsIds,
    );
  }

  if (profilePageWidgetRef && profileWidgets.length > 0) {
    await removeWidgetPluginsFromWidget(
      editorSDK,
      profilePageWidgetRef,
      widgetsIds,
    );
  }

  await removeWidgetsMenusItems(editorSDK, widgetsIds);
  await removeRoutesFromSettingsPage(editorSDK, widgetsIds);
  await removeRoutesFromProfilePage(editorSDK, widgetsIds);
};
